import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import StatusBadge from "@/components/StatusBadge";
import { QrCode, Camera, CheckCircle, SignOut, Package, MapPin, X, Keyboard } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ReceiverView() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useDocumentTitle("Confirm Delivery");
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [result, setResult] = useState(null);
  const [inbound, setInbound] = useState([]);
  const scannerRef = useRef(null);

  useEffect(() => {
    api.get("/inbound").then((r) => setInbound(r.data)).catch(() => {});
    return () => stopScan();
  }, []); // eslint-disable-line

  const stopScan = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScan = async () => {
    setScanning(true);
    setResult(null);
    setTimeout(async () => {
      try {
        const qr = new Html5Qrcode("qr-reader");
        scannerRef.current = qr;
        await qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 250 },
          (decoded) => { stopScan(); verify(decoded.trim()); },
          () => {}
        );
      } catch (e) {
        setScanning(false);
        toast.error("Camera unavailable — enter the code manually below");
      }
    }, 150);
  };

  const verify = async (code) => {
    if (!code) return;
    try {
      const { data } = await api.post(`/verify/${encodeURIComponent(code)}`);
      setResult({ ...data.shipment, already: data.already });
      if (data.already) toast.message("Already delivered");
      else toast.success("Delivery confirmed!");
      api.get("/inbound").then((r) => setInbound(r.data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      <div className="bg-slate-900 text-white px-5 pt-6 pb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-400">Receiver</div>
            <div className="font-heading text-2xl font-bold">{user.name}</div>
          </div>
          <button data-testid="receiver-logout" onClick={() => { logout(); navigate("/login"); }} className="h-10 w-10 rounded-md bg-white/10 flex items-center justify-center"><SignOut size={18} /></button>
        </div>
      </div>

      <div className="p-5 -mt-4 space-y-5 flex-1">
        {/* scan card */}
        <div className="bg-white border border-slate-200 rounded-md p-6 text-center">
          {!scanning && !result && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-slate-900 text-white flex items-center justify-center mb-4"><QrCode size={32} weight="duotone" /></div>
              <h2 className="font-heading text-xl font-bold text-slate-900">Confirm a Delivery</h2>
              <p className="text-sm text-slate-500 mt-1 mb-5">Scan the shipment QR code on arrival</p>
              <Button data-testid="start-scan" onClick={startScan} className="w-full h-14 text-base bg-slate-900 hover:bg-slate-800"><Camera size={22} className="mr-2" />Scan QR Code</Button>
            </>
          )}

          {scanning && (
            <div>
              <div id="qr-reader" className="rounded-md overflow-hidden mx-auto max-w-xs" />
              <Button data-testid="cancel-scan" variant="outline" onClick={stopScan} className="mt-4"><X size={16} className="mr-1.5" />Cancel</Button>
            </div>
          )}

          {result && (
            <div data-testid="verify-result" className="text-left">
              <div className="flex flex-col items-center text-center mb-4">
                <div className="h-16 w-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-3"><CheckCircle size={36} weight="fill" /></div>
                <h2 className="font-heading text-xl font-bold text-slate-900">{result.already ? "Already Delivered" : "Delivery Confirmed"}</h2>
                <div className="font-mono text-slate-500 mt-1">{result.code}</div>
              </div>
              <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
                <Row label="Product" value={result.product} />
                <Row label="Route" value={`${result.origin.name} → ${result.destination.name}`} />
                <Row label="Cargo" value={`${result.quantity_tons} tons`} />
                <Row label="CO₂ (finalized)" value={`${result.co2_kg} kg`} />
                <Row label="Status" value={<StatusBadge status={result.status} />} />
              </div>
              <Button data-testid="scan-another" onClick={() => setResult(null)} className="w-full mt-4 bg-slate-900 hover:bg-slate-800">Scan Another</Button>
            </div>
          )}
        </div>

        {/* manual entry */}
        {!result && (
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-slate-500 mb-3"><Keyboard size={14} />Enter code manually</div>
            <div className="flex gap-2">
              <Input data-testid="manual-code" value={manual} onChange={(e) => setManual(e.target.value.toUpperCase())} placeholder="CT-XXXXXXXX" className="font-mono" />
              <Button data-testid="verify-manual" onClick={() => verify(manual.trim())} className="bg-slate-900 hover:bg-slate-800">Verify</Button>
            </div>
          </div>
        )}

        {/* inbound list */}
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500 px-1 mb-2">Inbound Shipments</div>
          <div className="space-y-2">
            {inbound.slice(0, 8).map((s) => (
              <div key={s.id} data-testid={`inbound-${s.code}`} className="bg-white border border-slate-200 rounded-md p-3 flex items-center justify-between">
                <div>
                  <div className="font-mono text-sm font-medium text-slate-900">{s.code}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={12} />{s.origin.name} → {s.destination.name}</div>
                </div>
                <StatusBadge status={s.status} />
              </div>
            ))}
            {inbound.length === 0 && <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-slate-400 text-sm">No inbound shipments.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-xs uppercase tracking-wider font-bold text-slate-400">{label}</span>
      <span className="text-sm text-slate-800 font-medium">{value}</span>
    </div>
  );
}
